import moment from "moment"
import { useSelector } from "react-redux"
import React, { useEffect, useState } from "react"
import { resolveMediaUrl } from "../../utils"

/** Thumbnail + fullscreen preview (replaces react-fancybox, which bundles incompatible React 15/16.) */
const ReportFileImage = ({ file }) => {
    const src = resolveMediaUrl(file)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!open) return
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false)
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [open])

    if (!file) return null

    return (
        <>
            <button
                type="button"
                className="report-file-thumbnail"
                onClick={() => setOpen(true)}
                aria-label="View attachment"
            >
                <img src={src} alt="" />
            </button>
            {open && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setOpen(false)}
                    role="presentation"
                >
                    <button
                        type="button"
                        className="absolute top-4 right-4 z-[101] rounded bg-white/10 px-3 py-1 text-2xl leading-none text-white hover:bg-white/20"
                        onClick={(e) => {
                            e.stopPropagation()
                            setOpen(false)
                        }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <img
                        src={src}
                        alt="Report attachment"
                        className="max-h-[90vh] max-w-full object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    )
}

const CourseHistory = ({ history, otherName }) => {
    const { user } = useSelector((state) => state.auth)
    const { currentProfile } = useSelector((state) => state.profile)
    const EventCard = ({ tone = "slate", title, subtitle, at, children }) => {
        const toneClass = {
            blue: "bg-blue-50 border-blue-200",
            green: "bg-green-50 border-green-200",
            red: "bg-red-50 border-red-200",
            amber: "bg-amber-50 border-amber-200",
            slate: "bg-slate-50 border-slate-200",
        }[tone] || "bg-slate-50 border-slate-200";

        return (
            <div className={`border rounded-xl p-3 ${toneClass}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                    <p className="text-sm font-semibold text-nt-blue">{title}</p>
                    <span className="text-xs text-nt-grey">{moment(at).format("YYYY-MM-DD HH:mm")}</span>
                </div>
                {subtitle ? <p className="text-xs text-nt-grey mt-0.5">{subtitle}</p> : null}
                {children ? <div className="mt-2.5 text-sm text-nt-blue">{children}</div> : null}
            </div>
        );
    };

    const LabelValue = ({ label, value }) => (
        <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
            <span className="text-nt-grey text-xs sm:text-sm font-medium min-w-[82px]">{label}</span>
            <span className="text-nt-blue text-sm">{value || "-"}</span>
        </div>
    );

    const RescheduleItem = ({ reason, by, message, date, at }) => (
        <EventCard
            tone="blue"
            at={at}
            title={`${by} rescheduled this course`}
            subtitle={`New time: ${moment(date).format("YYYY-MM-DD HH:mm")}`}
        >
            <div className="space-y-1.5">
                <LabelValue label="Reason" value={reason} />
                {message && message.trim() ? <LabelValue label="Message" value={message} /> : null}
            </div>
        </EventCard>
    );

    const ApproveItem = ({ by, at }) => (
        <EventCard
            tone="green"
            at={at}
            title={by ? `${by} approved the payment` : "System approved the payment"}
        />
    );

    const CancelItem = ({ reason, message, by, at }) => (
        <EventCard
            tone="red"
            at={at}
            title={`${by} cancelled this course`}
        >
            <div className="space-y-1.5">
                <LabelValue label="Reason" value={reason} />
                {message && message.trim() ? <LabelValue label="Message" value={message} /> : null}
            </div>
        </EventCard>
    );

    const ReportItem = ({ reason, by, at, message, file }) => (
        <EventCard
            tone="amber"
            at={at}
            title={`${by} reported an issue`}
        >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="space-y-1.5">
                    <LabelValue label="Reason" value={reason} />
                    {(message && message.trim() !== "") ? <LabelValue label="Details" value={message} /> : null}
                </div>
                {file ? <ReportFileImage file={file} /> : null}
            </div>
        </EventCard>
    );

    return <div className="mt-6">
        {history.length > 0 ? (
            <h3 className="text-nt-blue mb-3 font-semibold">History</h3>
        ) : null}
        <div className="space-y-2.5">
            {history.map((v, i) => {
                if (v.type === "report") {
                    return <ReportItem key={i} reason={v.reason} at={v.createdAt} file={v.file?.path} by={currentProfile._id === v.by ? user.firstName : otherName} message={v.more} />
                } else if (v.type === "cancel") {
                    return <CancelItem key={i} reason={v.reason} message={v.message} at={v.createdAt} by={currentProfile._id === v.by ? user.firstName : otherName} />
                } else if (v.type === "approve") {
                    return <ApproveItem key={i} reason={v.reason} at={v.createdAt} by={currentProfile._id === v.by ? user.firstName : otherName} />
                } else if (v.type === "reschedule") {
                    return <RescheduleItem
                        key={i}
                        reason={v.reason}
                        at={v.createdAt}
                        date={v.newDate}
                        message={v.message}
                        by={currentProfile._id === v.by ? user.firstName : otherName} />
                }
                return null;
            })}
        </div>
    </div>
}

export { CourseHistory }